import {
  parseWorkspaceResourceHref,
  workspaceResourceHref,
} from "@playbookai/types";

/** What activating a notification should do. */
export type NotificationTarget =
  | {
      readonly kind: "module-entity";
      readonly moduleId: string;
      readonly entityId: string;
      readonly search?: string;
    }
  | { readonly kind: "navigate"; readonly href: string };

/**
 * Resolve a notification's `href` into an action the shell can take (PB-1583).
 *
 * A producer cannot address a module workspace directly: SOLVE lives at
 * `/app/solve/<tabInstance>/<solveId>` and the tab instance is minted by THIS
 * process, so a server that emitted the literal route would have to invent one.
 * It emits a REFERENCE instead — `/app/solve/<solveId>` — and the shell turns
 * that into a real route through the entity-tab contract it already has, which
 * knows each module's route policy (mint an instance for SOLVE and VSM, none
 * for Hoshin) and which opens-or-focuses rather than duplicating a tab.
 *
 * Everything else navigates literally, which is what every notification with an
 * href did before this existed. Three cases fall through on purpose:
 *
 *  - a path that is already a resolved route (`/app/lama/assess?waveId=…`) —
 *    `parseWorkspaceResourceHref` requires exactly two segments after `/app`,
 *    so a deeper path is never truncated to its first two;
 *  - a module the deployment has DISABLED — its routes are not mounted, so the
 *    entity-tab open would create a pill pointing at nothing. Navigating lets
 *    the shell's own `path="*"` unavailable state answer instead;
 *  - anything not under `/app` at all (`/admin/roles`, `/auth/signed-out`).
 *
 * Pure, and shared by BOTH notification surfaces — the bell tray and the
 * full-page feed — because the tray was the only one that navigated at all and
 * two implementations of "open the shared resource" is how they diverge.
 *
 * Every VSM reference opens in Edit mode (PB-5441 BUG-20), so `eventType` no
 * longer changes the destination; it stays for the callers that pass it.
 */
export function resolveNotificationTarget(
  href: string,
  enabledModuleIds: readonly string[],
  _eventType?: string,
): NotificationTarget {
  const reference = parseWorkspaceResourceHref(href);
  if (reference && enabledModuleIds.includes(reference.moduleId)) {
    return {
      kind: "module-entity",
      moduleId: reference.moduleId,
      entityId: reference.entityId,
      ...(reference.moduleId === "vsm" ? { search: vsmSearch(href) } : {}),
    };
  }
  return { kind: "navigate", href };
}

/**
 * The address a notification row's link carries: what opening it in a new
 * browser tab, or a copied link, opens. A module reference stays a reference
 * (`/app/<module>/<id>`), because the shell's start-up opens one under a
 * numbered tab (d1); it never names a tab number of its own. It carries only
 * the query a click would: `mode=edit` and the comment params for VSM. Anything
 * else is the href as sent. Clicks still take the original href.
 */
export function notificationHref(
  href: string,
  enabledModuleIds: readonly string[],
  eventType?: string,
): string {
  const target = resolveNotificationTarget(href, enabledModuleIds, eventType);
  if (target.kind !== "module-entity") return href;
  const reference = workspaceResourceHref(target.moduleId, target.entityId);
  return reference === null ? href : `${reference}${target.search ?? ""}`;
}

/** The comment link params the VSM canvas reads; every other param is dropped. */
const VSM_COMMENT_PARAMS = ["commentWidget", "commentThread"] as const;

/**
 * `mode=edit` plus the comment link params from the reference's own query, and
 * nothing else, so a producer cannot steer the tab anywhere the shell did not
 * choose.
 */
function vsmSearch(href: string): string {
  const path = href.split("#", 1)[0] ?? "";
  const at = path.indexOf("?");
  const from = new URLSearchParams(at === -1 ? "" : path.slice(at + 1));
  const search = new URLSearchParams({ mode: "edit" });
  for (const name of VSM_COMMENT_PARAMS) {
    const value = from.get(name);
    if (value !== null) search.set(name, value);
  }
  return `?${search.toString()}`;
}
